import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Box, Stack, Typography, Chip } from "@mui/material";
import { AccountBalanceWalletOutlined } from "@mui/icons-material";
import { getServiceChargeDetail, evidenceUrl } from "api/financial";
import { getMyServiceChargeDetail, branchCashEvidenceUrl } from "api/branchCash";
import { MONO, money, fmtDate, PaymentLines, SlipLightbox, ChangeGivenBox, RelatedTransactionsBox, HeroMetaHeader, DetailDialogShell } from "./receiptParts";
import TransactionDetailDialog from "./TransactionDetailDialog";

// Detail for one BranchCashServiceCharge (a staff-recorded ad-hoc branch cash
// charge with no fixed rate-table price, e.g. bulk delivery, shipping parcels
// back to Thailand) — twin of ShippingApprovalDetailDialog, minus packages.
// `mode="financial"` (default) is the manager drill-down (any station).
// `mode="branch"` is the station-scoped Branch Cash page (own stations only).
export default function ServiceChargeDetailDialog({
  open,
  serviceChargeId,
  onClose,
  onApprove,
  onReject,
  busy = false,
  mode = "financial",
}) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [txId, setTxId] = useState(null);
  const fetchDetail = mode === "branch" ? getMyServiceChargeDetail : getServiceChargeDetail;
  const getEvidenceUrl = mode === "branch" ? branchCashEvidenceUrl : evidenceUrl;

  useEffect(() => {
    if (!open || !serviceChargeId) return;
    setLoading(true);
    setD(null);
    fetchDetail(serviceChargeId)
      .then((res) => setD(res && res.success ? res.data : null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, serviceChargeId, mode]);

  const sc = d?.serviceCharge;
  const walletCurrency = sc?.walletCurrency || "THB";
  const walletPortionAmount = sc?.walletPortionAmount || 0;
  const fullyPaidByWallet = sc && (sc.lines || []).length === 0 && walletPortionAmount > 0;

  return (
    <>
      <DetailDialogShell
        t={t}
        open={open}
        onClose={onClose}
        title={t("branchCash.serviceChargeDetail")}
        subtitle={sc?.reference}
        loading={loading}
        empty={!sc}
        actions={
          sc && sc.status === "pending_approval" && onApprove && onReject ? (
            <>
              <Button variant="outlined" color="error" disabled={busy} onClick={() => onReject(serviceChargeId)}>
                {t("financial.reject")}
              </Button>
              <Button variant="contained" color="secondary" disabled={busy} onClick={() => onApprove(serviceChargeId)}>
                {t("financial.approve")}
              </Button>
            </>
          ) : null
        }
      >
        {sc && (
          <>
            <HeroMetaHeader
              heroLabel={t("pickup.payment.feeDue")}
              heroValue={money(sc.feeTHB)}
              heroExtra={
                sc.approvalReason ? (
                  <Chip size="small" color="warning" sx={{ mt: 0.5 }} label={t(`financial.reason.${sc.approvalReason}`)} />
                ) : undefined
              }
              metaRows={[
                { label: t("branchCash.serviceChargePurpose"), value: sc.purpose },
                { label: t("financial.customer"), value: d.customer ? `${d.customer.name}${d.customer.whatsapp ? ` · ${d.customer.whatsapp}` : ""}` : "—" },
                { label: t("financial.station"), value: sc.stationId },
                { label: t("branchCash.requestedBy"), value: sc.requestedBy?.name },
                { label: t("financial.date"), value: fmtDate(sc.createdAt) },
              ]}
            />

            {walletPortionAmount > 0 && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 2, p: 1.5, borderRadius: "12px", border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccountBalanceWalletOutlined fontSize="small" color="secondary" />
                  <Box>
                    <Typography sx={{ fontSize: 11.5 }} color="text.secondary">
                      {(fullyPaidByWallet ? t("financial.paidByWallet") : t("financial.walletPortion")) + (walletCurrency !== "THB" ? ` (${walletCurrency})` : "")}
                    </Typography>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 16 }}>
                      {money(walletPortionAmount, walletCurrency)}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            )}

            <PaymentLines
              t={t}
              lines={sc.lines}
              onEnlarge={setLightbox}
              getEvidenceUrl={getEvidenceUrl}
              emptyLabel={fullyPaidByWallet ? t("financial.fullyPaidByWallet") : undefined}
            />

            <ChangeGivenBox
              t={t}
              lines={sc.changeLines}
              labelFor={(l) => (l.source === "wallet_credit" ? t("pickup.payment.changeWallet") : t("pickup.payment.changeCash"))}
            />

            {sc.status !== "pending_approval" && (
              <RelatedTransactionsBox
                t={t}
                ok={sc.status === "completed"}
                statusLabel={t(`financial.${sc.status === "completed" ? "completed" : "rejected"}`)}
                subLabel={
                  sc.status === "completed" && sc.approvedBy?.name
                    ? `${t("financial.approvedBy")}: ${sc.approvedBy.name}${sc.approvedBy.at ? ` · ${fmtDate(sc.approvedBy.at)}` : ""}`
                    : sc.status === "rejected" && sc.rejectionReason
                    ? `${t("financial.rejectionReason")}: ${sc.rejectionReason}`
                    : null
                }
                refs={d?.transactionRefs || []}
                onView={setTxId}
              />
            )}
          </>
        )}
      </DetailDialogShell>

      <TransactionDetailDialog mode={mode} open={!!txId} txId={txId} onClose={() => setTxId(null)} />

      <SlipLightbox filename={lightbox} onClose={() => setLightbox(null)} t={t} getEvidenceUrl={getEvidenceUrl} />
    </>
  );
}
